import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {AnnouncementsPopUp} from './announcementsPopUp/AnnouncementsPopUp';
import {AnnouncementType} from '../../../types/AnnouncementType';
import {
  dismissAnnouncement,
  locallyDismissAnnouncement,
  selectLocallyDismissedAnnouncements,
} from '../../../redux/slices/campusLocationSlice';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';

interface AnnouncementsManagerProps {
  announcementsData: AnnouncementType[];
}

export const AnnouncementsManager: FC<AnnouncementsManagerProps> = memo(
  ({announcementsData}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visible, setVisible] = useState(false);
    const dispatch = useAppDispatch();
    const locallyDismissedIds = useAppSelector(
      selectLocallyDismissedAnnouncements,
    );

    const fullScreenAnnouncements = useMemo(() => {
      return announcementsData.filter(
        (a) =>
          a.isFullScreenAdEnabled &&
          !a.isDismissed &&
          !locallyDismissedIds.includes(a.announcementId),
      );
    }, [announcementsData, locallyDismissedIds]);

    useEffect(() => {
      if (fullScreenAnnouncements.length > 0) {
        setCurrentIndex(0);
        setVisible(true);
      }
    }, [announcementsData, fullScreenAnnouncements]);

    const current = fullScreenAnnouncements[currentIndex];

    const handleClose = useCallback(() => {
      if (!current) return;

      dispatch(
        locallyDismissAnnouncement({announcementId: current.announcementId}),
      );
      dispatch(dismissAnnouncement({announcementId: current.announcementId}));

      if (currentIndex < fullScreenAnnouncements.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setVisible(false);
      }
    }, [current, currentIndex, dispatch, fullScreenAnnouncements.length]);

    if (!visible || fullScreenAnnouncements.length === 0) {
      return null;
    }

    return (
      <AnnouncementsPopUp
        isModalVisible={visible}
        onModalClose={handleClose}
        title={current?.fullScreenHeader}
        body={current?.fullScreenBody}
        coverImg={current?.fullScreenImageUrl}
        btnText={current?.fullScreenButtonTitle}
        btnLink={
          current?.fullScreenExternalLink || current?.fullScreenPdfAttachUrl
        }
      />
    );
  },
);
